import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { updateLocaleOptions } from 'primereact/api';
import React, { useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import Splash from './components/layout/Splash.jsx';
import BaseRoute from './components/misc/BaseRoute.jsx';
import CurrentUser from './middleware/CurrentUser.jsx';
import RedirectIfAuthenticated from './middleware/RedirectIfAuthenticated.jsx';
import RedirectIfEmailVerified from './middleware/RedirectIfEmailVerified.jsx';
import RedirectIfTwoFactor from './middleware/RedirectIfTwoFactor.jsx';
import RedirectIfValidated from './middleware/RedirectIfValidated.jsx';
import RequireAuth from './middleware/RequireAuth.jsx';
import RequireEmailVerified from './middleware/RequireEmailVerified.jsx';
import RequirePermission from './middleware/RequirePermission.jsx';
import RequireRole from './middleware/RequireRole.jsx';
import RequireTwoFactor from './middleware/RequireTwoFactor.jsx';
import Account from './pages/Account.jsx';
import ChangePassword from './pages/auth/ChangePassword.jsx';
import CheckEmail from './pages/auth/CheckEmail.jsx';
import ForgotPassword from './pages/auth/ForgotPassword.jsx';
import Login from './pages/auth/Login.jsx';
import Logout from './pages/auth/Logout.jsx';
import Register from './pages/auth/Register.jsx';
import TwoFactor from './pages/auth/TwoFactor.jsx';
import ValidateAccount from './pages/auth/ValidateAccount.jsx';
import VerifyEmail from './pages/auth/VerifyEmail.jsx';
import AddEditMeeting from './pages/calendar/AddEditMeeting.jsx';
import Calendar from './pages/calendar/Calendar.jsx';
import MeetingPreparation from './pages/calendar/MeetingPreparation.jsx';
import ViewMeeting from './pages/calendar/ViewMeeting.jsx';
import AddEditContact from './pages/contacts/AddEditContact.jsx';
import ContactsDashboard from './pages/contacts/ContactsDashboard.jsx';
import ListContacts from './pages/contacts/ListContacts.jsx';
import ViewContact from './pages/contacts/ViewContact.jsx';
import Dashboard from './pages/Dashboard.jsx';
import PageNotAllowed from './pages/misc/PageNotAllowed.jsx';
import PageNotFound from './pages/misc/PageNotFound.jsx';
import UserManagement from './pages/users/UserManagement.jsx';
import UserRoles from './pages/users/UserRoles.jsx';
import UserTickets from './pages/tickets/UserTickets.jsx';
import AdminTickets from './pages/tickets/AdminTickets.jsx';
import Icons from './pages/Icons.jsx';
import TicketDetail from './pages/tickets/TicketDetail.jsx';
import AdvancedSearchContacts from './pages/contacts/AdvancedSearchContacts.jsx';
import AdvancedSearchCalendarMeetings from './pages/calendar/AdvancedSearchCalendarMeetings.jsx';
import {
    PERMISSION_CALENDAR,
    PERMISSION_CONTACTS,
    PERMISSION_FILES,
    PERMISSION_LEADS,
    PERMISSION_OWNED_ORGANIZATIONS,
    PERMISSION_PARTNER_ORGANIZATIONS,
    PERMISSION_PROJECTS,
    PERMISSION_ROLES,
    PERMISSION_TICKETS_ADMIN,
    PERMISSION_TICKETS_USER,
    PERMISSION_USERS
} from './utils/constants.js';
import MeetingInvitation from './pages/calendar/MeetingInvitation.jsx';
import ImportContacts from './pages/contacts/ImportContacts.jsx';
import Organizations from './pages/organizations/Organizations.jsx';
import OrganizationDetail from './pages/organizations/OrganizationDetail.jsx';
import AddEditOrganization from './pages/organizations/AddEditOrganization.jsx';
import AdvancedSearchOrganizations from './pages/organizations/AdvancedSearchOrganizations.jsx';
import Projects from './pages/projects/Projects.jsx';
import ProjectDetail from './pages/projects/ProjectDetail.jsx';
import AddEditProject from './pages/projects/AddEditProject.jsx';
import AdvancedSearchProjects from './pages/projects/AdvancedSearchProjects.jsx';
import ImportFiles from './pages/files/ImportFiles.jsx';
import FilesDashboard from './pages/files/FilesDashboard.jsx';
import EditFile from './pages/files/EditFile.jsx';
import AdvancedSearchFiles from './pages/files/AdvancedSearchFiles.jsx';
import PrivacyPolicy from './pages/misc/PrivacyPolicy.jsx';
import TermsAndConditions from './pages/misc/TermsAndConditions.jsx';
import Homepage from './pages/Homepage.jsx';
import OrganizationsDashboard from './pages/organizations/OrganizationsDashboard.jsx';
import Leads from './pages/leads/Leads.jsx';
import LeadsDetail from './pages/leads/LeadsDetail.jsx';
import OrganizationKPIs from './pages/organizations/OrganizationKPIs.jsx';
import useHotjar from './hooks/useHotjar';
import useHotjarRouteTracking from './hooks/useHotjarRouteTracking';
import GlobalView from './pages/global-view/GlobalView.jsx';
import AdvancedSearchGlobal from './pages/global-view/AdvancedSearchGlobal.jsx';
import ContactDuplicates from './pages/contacts/ContactDuplicates.jsx';
import ContactDuplicatesDetail from './pages/contacts/ContactDuplicatesDetail.jsx';
import PersonalAssistant from './pages/speech/PersonalAssistant.jsx';
import PersonalChats from './pages/speech/PersonalChats.jsx';
import Notifications from './pages/Notifications.jsx';
import PushNotificationManager from './pages/auth/PushNotificationManager.jsx';

dayjs.extend(isSameOrBefore);

updateLocaleOptions(
    {
        firstDayOfWeek: 1
    },
    'en'
);

const App = () => {
    useHotjar();
    useHotjarRouteTracking();

    const [splash, setSplash] = useState(true);

    toast.onChange((payload) => {
        if (payload.content === '') {
            const { message } = payload.data;

            toast.update(payload.id, {
                render: () => message
            });
        }
    });

    useEffect(() => {
        const splashTimeout = setTimeout(() => {
            setSplash(false);
        }, 1000);

        return () => {
            clearTimeout(splashTimeout);
        };
    }, []);

    return (
        <>
            {splash && <Splash />}
            {!splash && (
                <>
                    <Routes>
                        <Route path={'/calendar-invite'} element={<MeetingInvitation />} />
                        <Route element={<CurrentUser />}>
                            <Route path='/' element={<Homepage />} />
                            <Route element={<RedirectIfAuthenticated redirectTo='/two-factor' />}>
                                <Route path='login' element={<Login />} />
                                <Route path='forgot-password' element={<ForgotPassword />} />
                                <Route path='change-password' element={<ChangePassword />} />
                                <Route path='register' element={<Register />} />
                            </Route>
                        </Route>

                        <Route element={<CurrentUser />}>
                            <Route element={<RequireAuth redirectTo='/login' />}>
                                <Route element={<RedirectIfEmailVerified redirectTo='/dashboard' />}>
                                    <Route path='check-email' element={<CheckEmail />} />
                                    <Route path='verify-email' element={<VerifyEmail />} />
                                </Route>

                                <Route path='/logout' element={<Logout />} />

                                <Route element={<RequireEmailVerified redirectTo='/check-email' />}>
                                    <Route element={<RedirectIfTwoFactor redirectTo='/dashboard' />}>
                                        <Route path='/two-factor' element={<TwoFactor />} />
                                    </Route>
                                    <Route element={<RequireTwoFactor redirectTo='/two-factor' />}>
                                        <Route element={<RedirectIfValidated redirectTo='/dashboard' />}>
                                            <Route path='/validate' element={<ValidateAccount />} />
                                        </Route>

                                        <Route element={<RequireRole redirectTo='/validate' />}>
                                            {BaseRoute(<PageNotAllowed />, '/unauthorized')}
                                            {BaseRoute(<Account />, '/profile')}

                                            {BaseRoute(<Dashboard />, '/dashboard')}

                                            {BaseRoute(<PersonalAssistant />, '/personal-assistant/:id')}
                                            {BaseRoute(<PersonalAssistant />, '/personal-assistant')}
                                            {BaseRoute(<PersonalChats />, '/personal-assistant-history')}

                                            {BaseRoute(<Notifications />, '/notifications')}

                                            <Route
                                                element={
                                                    <RequirePermission
                                                        permissions={{ [PERMISSION_CONTACTS]: ['read'] }}
                                                    />
                                                }
                                            >
                                                {BaseRoute(<ContactsDashboard />, '/contacts')}
                                                {BaseRoute(<ListContacts />, '/contacts/list')}
                                                {BaseRoute(<AdvancedSearchContacts />, '/contacts/advanced-search')}
                                                {BaseRoute(<ViewContact />, '/contacts/view/:id')}
                                                {BaseRoute(<ContactDuplicates />, '/contacts/duplicates')}
                                                {BaseRoute(<ContactDuplicatesDetail />, '/contacts/duplicates/:id')}
                                                <Route
                                                    element={
                                                        <RequirePermission
                                                            permissions={{ [PERMISSION_CONTACTS]: ['create'] }}
                                                        />
                                                    }
                                                >
                                                    {BaseRoute(<AddEditContact />, '/contacts/add')}
                                                    {BaseRoute(<ImportContacts />, '/contacts/import')}
                                                </Route>
                                                <Route
                                                    element={
                                                        <RequirePermission
                                                            permissions={{ [PERMISSION_CONTACTS]: ['update'] }}
                                                        />
                                                    }
                                                >
                                                    {BaseRoute(<AddEditContact edit />, '/contacts/edit/:id')}
                                                </Route>
                                            </Route>

                                            <Route
                                                element={
                                                    <RequirePermission permissions={{ [PERMISSION_FILES]: ['read'] }} />
                                                }
                                            >
                                                {BaseRoute(<FilesDashboard />, '/files')}
                                                {BaseRoute(<AdvancedSearchFiles />, '/files/advanced-search')}
                                                <Route
                                                    element={
                                                        <RequirePermission
                                                            permissions={{ [PERMISSION_FILES]: ['create'] }}
                                                        />
                                                    }
                                                >
                                                    {BaseRoute(<ImportFiles />, '/files/import')}
                                                </Route>
                                                <Route
                                                    element={
                                                        <RequirePermission
                                                            permissions={{ [PERMISSION_FILES]: ['update'] }}
                                                        />
                                                    }
                                                >
                                                    {BaseRoute(<EditFile />, '/files/edit/:id')}
                                                </Route>
                                            </Route>

                                            <Route
                                                element={
                                                    <RequirePermission permissions={{ [PERMISSION_USERS]: ['read'] }} />
                                                }
                                            >
                                                {BaseRoute(<UserManagement />, '/users')}
                                            </Route>

                                            <Route
                                                element={
                                                    <RequirePermission permissions={{ [PERMISSION_ROLES]: ['read'] }} />
                                                }
                                            >
                                                {BaseRoute(<UserRoles />, '/roles')}
                                            </Route>

                                            <Route
                                                element={
                                                    <RequirePermission
                                                        permissions={{ [PERMISSION_TICKETS_USER]: ['read'] }}
                                                    />
                                                }
                                            >
                                                {BaseRoute(<UserTickets />, '/my-tickets')}
                                                {BaseRoute(<TicketDetail />, '/my-tickets/:id')}
                                            </Route>

                                            <Route
                                                element={
                                                    <RequirePermission
                                                        permissions={{ [PERMISSION_TICKETS_ADMIN]: ['read'] }}
                                                    />
                                                }
                                            >
                                                {BaseRoute(<AdminTickets />, '/tickets')}
                                                {BaseRoute(<TicketDetail />, '/tickets/:id')}
                                            </Route>

                                            <Route
                                                element={
                                                    <RequirePermission
                                                        permissions={{ [PERMISSION_CALENDAR]: ['read'] }}
                                                    />
                                                }
                                            >
                                                {BaseRoute(<Calendar />, '/calendar')}
                                                {BaseRoute(<ViewMeeting />, '/calendar/view/:id')}
                                                {BaseRoute(
                                                    <AdvancedSearchCalendarMeetings />,
                                                    '/calendar/advanced-search'
                                                )}

                                                <Route
                                                    element={
                                                        <RequirePermission
                                                            permissions={{ [PERMISSION_CALENDAR]: ['create'] }}
                                                        />
                                                    }
                                                >
                                                    {BaseRoute(<AddEditMeeting />, '/calendar/add')}
                                                </Route>
                                                <Route
                                                    element={
                                                        <RequirePermission
                                                            permissions={{ [PERMISSION_CALENDAR]: ['update'] }}
                                                        />
                                                    }
                                                >
                                                    {BaseRoute(<MeetingPreparation />, '/calendar/preparation/:id')}
                                                    {BaseRoute(<AddEditMeeting edit />, '/calendar/edit/:id')}
                                                </Route>
                                            </Route>

                                            <Route
                                                element={
                                                    <RequirePermission
                                                        permissions={{
                                                            [PERMISSION_PARTNER_ORGANIZATIONS]: ['read'],
                                                            [PERMISSION_OWNED_ORGANIZATIONS]: ['read']
                                                        }}
                                                        requireAny={true}
                                                    />
                                                }
                                            >
                                                {BaseRoute(<OrganizationsDashboard />, '/organizations')}
                                                {BaseRoute(<Organizations />, '/organizations/list')}
                                                {BaseRoute(<OrganizationDetail />, '/organizations/:id')}
                                                {BaseRoute(<OrganizationKPIs />, '/organizations/kpis/:id')}
                                                {BaseRoute(
                                                    <AdvancedSearchOrganizations />,
                                                    '/organizations/advanced-search'
                                                )}

                                                <Route
                                                    element={
                                                        <RequirePermission
                                                            permissions={{
                                                                [PERMISSION_PARTNER_ORGANIZATIONS]: ['create'],
                                                                [PERMISSION_OWNED_ORGANIZATIONS]: ['create']
                                                            }}
                                                            requireAny={true}
                                                        />
                                                    }
                                                >
                                                    {BaseRoute(<AddEditOrganization />, '/organizations/add')}
                                                </Route>

                                                <Route
                                                    element={
                                                        <RequirePermission
                                                            permissions={{
                                                                [PERMISSION_PARTNER_ORGANIZATIONS]: ['update'],
                                                                [PERMISSION_OWNED_ORGANIZATIONS]: ['update']
                                                            }}
                                                            requireAny={true}
                                                        />
                                                    }
                                                >
                                                    {BaseRoute(
                                                        <AddEditOrganization isEditMode />,
                                                        '/organizations/edit/:id'
                                                    )}
                                                </Route>
                                            </Route>

                                            <Route
                                                element={
                                                    <RequirePermission
                                                        permissions={{
                                                            [PERMISSION_PROJECTS]: ['read']
                                                        }}
                                                    />
                                                }
                                            >
                                                {BaseRoute(<Projects />, '/projects')}
                                                {BaseRoute(<ProjectDetail />, '/projects/:id')}
                                                {BaseRoute(<AdvancedSearchProjects />, '/projects/advanced-search')}

                                                <Route
                                                    element={
                                                        <RequirePermission
                                                            permissions={{
                                                                [PERMISSION_PROJECTS]: ['create']
                                                            }}
                                                        />
                                                    }
                                                >
                                                    {BaseRoute(<AddEditProject />, '/projects/add')}
                                                </Route>

                                                <Route
                                                    element={
                                                        <RequirePermission
                                                            permissions={{
                                                                [PERMISSION_PROJECTS]: ['update']
                                                            }}
                                                        />
                                                    }
                                                >
                                                    {BaseRoute(<AddEditProject isEditMode />, '/projects/edit/:id')}
                                                </Route>
                                            </Route>

                                            <Route
                                                element={
                                                    <RequirePermission
                                                        permissions={{
                                                            [PERMISSION_LEADS]: ['read']
                                                        }}
                                                    />
                                                }
                                            >
                                                {BaseRoute(<Leads />, '/leads')}
                                                {BaseRoute(<LeadsDetail />, '/leads/:id')}
                                            </Route>

                                            <Route path='/global-view' element={<GlobalView />} />
                                            {BaseRoute(<AdvancedSearchGlobal />, '/global-view/advanced-search')}
                                        </Route>
                                    </Route>
                                </Route>
                            </Route>
                        </Route>

                        {import.meta.env.VITE_APP_ENV === 'local' && <Route path='/icons' element={<Icons />} />}

                        <Route path='*' element={<PageNotFound />} />

                        <Route path={'/privacy-policy'} element={<PrivacyPolicy />} />
                        <Route path={'/terms-and-conditions'} element={<TermsAndConditions />} />
                    </Routes>
                    <ToastContainer
                        position='top-center'
                        autoClose={3000}
                        hideProgressBar
                        closeOnClick
                        draggable={false}
                        rtl={false}
                        theme='light'
                    />
                    {import.meta.env.VITE_APP_ENV !== 'local' && <PushNotificationManager />}
                </>
            )}
        </>
    );
};

export default App;
